// The desktop app's facts for the page: inside the shell (apps/desktop, its preload's window.gcDesktop) the page runs
// in a Chromium of its own on the player's machine. The graphics defaults stay the browser's (the shell never picks a
// heavier one); the shell's launch options can show the performance readout from the start and build the likely
// scenes out of view, whose kept scenes' and idle models' budgets then scale with the machine's memory
// (navigator.deviceMemory stops at 8 GB). Outside the shell this is null and nothing changes.

import type { TelShell } from '@gc/core';

export interface DesktopInfo {
  /** the machine's memory in GB (0 where the shell did not say) */
  memoryGB: number;
  /** the Steam overlay's state in the shell's words ('on (in-process gpu, dcomp off)', 'off', 'off (no Steam)'), or
   *  null from a shell that does not say */
  overlay: string | null;
  /** 'custom': the page's top bar is the window's title bar, with the system's buttons over one end */
  frame: 'custom' | 'native';
  /** the system buttons' strip height in CSS pixels (custom frame) */
  titleBarHeight: number;
  /** which end of the title bar the system's buttons take: 'right' (Windows), 'left' (macOS's traffic lights) */
  controlsSide: 'left' | 'right';
  /** the buttons' strip width in CSS pixels where the page cannot measure it (macOS), else 0 */
  controlsWidth: number;
  /** launch options: the small performance readout from the start, the likely scenes built out of view */
  readout: boolean;
  prebuild: boolean;
}

interface Bridge {
  memoryGB?: unknown;
  overlay?: unknown;
  frame?: unknown;
  titleBarHeight?: unknown;
  controlsSide?: unknown;
  controlsWidth?: unknown;
  readout?: unknown;
  prebuild?: unknown;
}

export function desktopInfo(): DesktopInfo | null {
  const b = (globalThis as { gcDesktop?: Bridge }).gcDesktop;
  if (!b || typeof b !== 'object') return null;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
  return {
    memoryGB: num(b.memoryGB),
    overlay: typeof b.overlay === 'string' ? b.overlay : null,
    frame: b.frame === 'custom' ? 'custom' : 'native',
    titleBarHeight: num(b.titleBarHeight),
    controlsSide: b.controlsSide === 'left' ? 'left' : 'right',
    controlsWidth: num(b.controlsWidth),
    readout: b.readout === true,
    prebuild: b.prebuild === true,
  };
}

/** Chromium's GPU state as the shell reads it (app.getGPUFeatureStatus): 'enabled', 'disabled_software' and so on. */
export interface DesktopGpu {
  compositing: string;
  webgpu: string;
  webgl: string;
  raster: string;
  overlay: string;
}

/** The shell's GPU state, or null outside the shell or from a shell that does not say. */
export async function desktopGpu(): Promise<DesktopGpu | null> {
  const b = (globalThis as { gcDesktop?: { gpu?: () => Promise<DesktopGpu | null> } }).gcDesktop;
  if (!b || typeof b.gpu !== 'function') return null;
  try {
    return (await b.gpu()) ?? null;
  } catch {
    return null;
  }
}

/** The overlay's state as a short key for the telemetry: on, on+gpuprocess, on+dcomp, on+invalidate (or a mix), off,
 *  nosteam; '?' from a shell that does not say. */
export function overlayKey(words: string | null): string {
  if (!words) return '?';
  if (words.startsWith('off (no Steam')) return 'nosteam';
  if (words.startsWith('off')) return 'off';
  let k = 'on';
  if (words.includes('gpu process')) k += '+gpuprocess';
  if (words.includes('dcomp on')) k += '+dcomp';
  if (words.includes('16 ms repaint')) k += '+invalidate';
  return k;
}

/** the shell's GPU state, read at the start and every minute (the telemetry and the readout read it from here) */
let gpuNow: DesktopGpu | null = null;
let gpuTimer = 0;
export function desktopGpuNow(): DesktopGpu | null {
  if (!gpuTimer && desktopInfo()) {
    const read = () => void desktopGpu().then((g) => (gpuNow = g));
    read();
    gpuTimer = window.setInterval(read, 60_000);
  }
  return gpuNow;
}

/** The desktop app's figures for a telemetry window: whether it is the desktop app, its overlay and Chromium's
 *  compositing; nothing in a browser. */
export function desktopTelemetry(): { desktop?: true; overlay?: string; compositing?: string } {
  const d = desktopInfo();
  if (!d) return {};
  const g = desktopGpuNow();
  return { desktop: true, overlay: overlayKey(d.overlay), ...(g?.compositing ? { compositing: g.compositing } : {}) };
}

/** the shell's facts and timeline (gcDesktop.shellState), read every 15 s for the telemetry's batches */
let shellNow: TelShell | null = null;
let shellTimer = 0;
/** The desktop app's state for the telemetry (its run's facts, Discord's state, its latest events), or null in a
 *  browser, from a shell that does not say, or before the first read. */
export function desktopShellNow(): TelShell | null {
  const b = (globalThis as { gcDesktop?: { shellState?: () => Promise<TelShell | null> } }).gcDesktop;
  if (!b || typeof b.shellState !== 'function') return null;
  if (!shellTimer) {
    const read = () =>
      void b.shellState!()
        .then((s) => (shellNow = s && typeof s === 'object' ? s : null))
        .catch(() => undefined);
    read();
    shellTimer = window.setInterval(read, 15_000);
  }
  return shellNow;
}
